import { formatUserFacingError } from '@/lib/errors'
import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { mailingListService } from '@/features/mailing-list'
import type { MailingListMemberDocument, MailingListSegment } from '@/types'

export function useEmailPreferences() {
  const { user } = useAuth()
  const email = user?.email
  const [member, setMember] = useState<(MailingListMemberDocument & { id: string }) | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!email) {
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const found = await mailingListService.getMemberByEmail(email)
      setMember(found)
    } catch (err) {
      setError(formatUserFacingError(err, 'Failed to load preferences.'))
    } finally {
      setLoading(false)
    }
  }, [email])

  useEffect(() => {
    void load()
  }, [load])

  async function updateSegments(segments: MailingListSegment[]): Promise<void> {
    if (!member) return
    setSaving(true)
    try {
      await mailingListService.updateSegments(member.id, segments)
      setMember({ ...member, segments })
    } catch (err) {
      setError(formatUserFacingError(err, 'Failed to update preferences.'))
    } finally {
      setSaving(false)
    }
  }

  async function unsubscribeAll(): Promise<void> {
    if (!member) return
    setSaving(true)
    try {
      await mailingListService.unsubscribe(member.id)
      setMember({ ...member, isSubscribed: false })
    } catch (err) {
      setError(formatUserFacingError(err, 'Failed to unsubscribe.'))
    } finally {
      setSaving(false)
    }
  }

  async function resubscribeAll(): Promise<void> {
    if (!member) return
    setSaving(true)
    try {
      await mailingListService.resubscribe(member.id)
      setMember({ ...member, isSubscribed: true })
    } catch (err) {
      setError(formatUserFacingError(err, 'Failed to re-subscribe.'))
    } finally {
      setSaving(false)
    }
  }

  return { member, loading, saving, error, updateSegments, unsubscribeAll, resubscribeAll, refresh: load }
}
